import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
    }
    // Normalize FastAPI/Pydantic 422 detail arrays into a single string
    // so callers can safely pass err.response.data.detail into toast.error()
    // without React crashing on "Objects are not valid as a React child".
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      const msg = detail
        .map((d) => (typeof d === "string" ? d : d?.msg ? d.msg : JSON.stringify(d)))
        .join("; ");
      error.response.data.detail = msg;
    } else if (detail && typeof detail === "object") {
      error.response.data.detail = detail.msg || JSON.stringify(detail);
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  getMe: () => api.get("/auth/me"),
  updateProfile: (data) => api.put("/auth/profile", data),
  verifyEmail: (data) => api.post("/auth/verify-email", data),
  resendVerification: (data) => api.post("/auth/resend-verification", data),
  forgotPassword: (email) => api.post("/auth/password/forgot", { email }),
  resetPassword: (token, new_password) => api.post("/auth/password/reset", { token, new_password }),
  getSlugStatus: () => api.get("/auth/profile/slug"),
  updateSlug: (slug) => api.put("/auth/profile/slug", { slug }),
  getSignupConfig: () => api.get("/auth/signup-config"),
};

export const siteGateAPI = {
  status: () => api.get("/site-gate/status"),
  verify: (password) => api.post("/site-gate/verify", { password }),
  check: (token) => api.post("/site-gate/check", { token }),
};

export const marketplaceAPI = {
  getListings: (params) => api.get("/marketplace/listings", { params }),
  getListing: (id) => api.get(`/marketplace/listings/${id}`),
  incrementListingView: (id) => api.post(`/marketplace/listings/${id}/view`),
  createListing: (data) => api.post("/marketplace/listings", data),
  updateListing: (id, data) => api.put(`/marketplace/listings/${id}`, data),
  deleteListing: (id) => api.delete(`/marketplace/listings/${id}`),
  getMyListings: () => api.get("/marketplace/my-listings"),
  createOffer: (data) => api.post("/marketplace/offers", data),
  getSentOffers: () => api.get("/marketplace/offers/sent"),
  getReceivedOffers: () => api.get("/marketplace/offers/received"),
  respondToOffer: (offerId, data) => api.put(`/marketplace/offers/${offerId}/respond`, data),
  requestShipping: (data) => api.post("/marketplace/shipping-request", data),
};

export const reviewsAPI = {
  getForSeller: (sellerId, kind) => api.get(`/marketplace/reviews/${sellerId}`, { params: kind ? { kind } : {} }),
  create: (data) => api.post("/marketplace/reviews", data),
  update: (id, data) => api.put(`/marketplace/reviews/${id}`, data),
  remove: (id) => api.delete(`/marketplace/reviews/${id}`),
  report: (id, reason) => api.post(`/marketplace/reviews/${id}/report`, { reason }),
  listReported: () => api.get("/marketplace/reviews-reported/all"),
  moderate: (id, action) => api.post(`/marketplace/reviews/${id}/moderate`, { action }),
};

export const servicesAPI = {
  getCategories: () => api.get("/services/categories"),
  getListings: (params) => api.get("/services/listings", { params }),
  createListing: (data) => api.post("/services/listings", data),
  getMyListings: () => api.get("/services/my-listings"),
  getStorefront: (userId) => api.get(`/services/storefront/${userId}`),
  getStorefrontSummary: (userId) => api.get(`/services/storefront-summary/${userId}`),
  getMyStorefront: () => api.get("/services/my-storefront"),
  updateMyStorefront: (data) => api.put("/services/my-storefront", data),
  checkSlug: (slug) => api.get("/services/slug-available", { params: { slug } }),
  sendQuote: (data) => api.post("/services/quote", data),
  // admin
  adminListCategories: () => api.get("/services/admin/categories"),
  adminCreateCategory: (data) => api.post("/services/admin/categories", data),
  adminUpdateCategory: (id, data) => api.put(`/services/admin/categories/${id}`, data),
  adminDeleteCategory: (id) => api.delete(`/services/admin/categories/${id}`),
};

export const shippingAPI = {
  getCategories: () => api.get("/shipping/categories"),
  calculateQuote: (data) => api.post("/shipping/quote", data),
  orderSupplies: (data) =>
    api.post(
      `/shipping/supplies/order?listing_id=${data.listing_id}&product_category=${data.product_category}&box_size=${data.box_size}`
    ),
};

export const offersAPI = {
  saveListing: (listing_id) =>
    api.post("/offers/saved-listings/simple", { listing_id }),
  checkSavedListing: (listing_id) =>
    api.get(`/offers/saved-listings/check/${listing_id}`),
  getSavedListings: () => api.get("/offers/saved-listings"),
  deleteSavedListing: (saved_id) =>
    api.delete(`/offers/saved-listings/${saved_id}`),
  respond: (offerId, body) => api.put(`/offers/${offerId}/respond`, body),
  get: (offerId) => api.get(`/offers/${offerId}`),
  forConversation: (conversationId) => api.get(`/offers/conversation/${conversationId}`),
};

export const forumAPI = {
  getPosts: (params) => api.get("/forum/posts", { params }),
  getPost: (id) => api.get(`/forum/posts/${id}`),
  createPost: (data) => api.post("/forum/posts", data),
  createReply: (postId, data) => api.post(`/forum/posts/${postId}/replies`, data),
  toggleLike: (postId) => api.post(`/forum/posts/${postId}/like`),
  getSpotlight: () => api.get("/forum/spotlight"),
};

export const forumProfileAPI = {
  batch: (userIds) => api.post("/forum-profile/batch", { user_ids: userIds }),
  getMySettings: () => api.get("/forum-profile/me/settings"),
  updateMySettings: (data) => api.put("/forum-profile/me/settings", data),
  getMyAchievements: () => api.get("/forum-profile/me/achievements"),
  getProfileAchievements: (userId) => api.get(`/forum-profile/${userId}/profile-achievements`),
};

export const forumSpotlightAdminAPI = {
  list: () => api.get("/admin/forum/spotlight"),
  create: (data) => api.post("/admin/forum/spotlight", data),
  update: (id, data) => api.put(`/admin/forum/spotlight/${id}`, data),
  remove: (id) => api.delete(`/admin/forum/spotlight/${id}`),
  reorder: (order) => api.put("/admin/forum/spotlight/reorder", { order }),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/admin/forum/spotlight/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
};

export const socialAPI = {
  getFeed: (params) => api.get("/social/feed", { params }),
  getPosts: (params) => api.get("/social/posts", { params }),
  createPost: (data) => api.post("/social/posts", data),
  updatePost: (postId, data) => api.put(`/social/posts/${postId}`, data),
  createComment: (postId, data) => api.post(`/social/posts/${postId}/comments`, data),
  toggleLike: (postId) => api.post(`/social/posts/${postId}/like`),
  toggleSave: (postId) => api.post(`/social/posts/${postId}/save`),
  listSaved: () => api.get("/social/saved"),
  reportPost: (postId, reason) => api.post(`/social/posts/${postId}/report`, null, { params: { reason } }),
  deletePost: (postId) => api.delete(`/social/posts/${postId}`),
  toggleFollow: (userId) => api.post(`/social/users/${userId}/follow`),
  getPost: (id) => api.get(`/social/posts/${id}`),
  getStories: () => api.get("/social/stories"),
  getUserStories: (userId) => api.get(`/social/stories/${userId}`),
  createStory: (data) => api.post("/social/stories", data),
};

export const messagesAPI = {
  getConversations: (params) => api.get("/messages/conversations", { params }),
  getConversation: (id, params) => api.get(`/messages/conversations/${id}`, { params }),
  startConversation: (data) => api.post("/messages/conversations", data),
  sendMessage: (id, content) =>
    api.post(`/messages/conversations/${id}/messages`, { content }),
  getUnreadCount: () => api.get("/messages/unread-count"),
};

export const homeAPI = {
  getTrendingListings: (limit) =>
    api.get("/home/trending-listings", { params: { limit } }),
  getPopularForumPosts: (limit) =>
    api.get("/home/popular-forum-posts", { params: { limit } }),
  getRecentActivity: (limit) =>
    api.get("/home/recent-activity", { params: { limit } }),
  getAnnouncements: (limit) =>
    api.get("/home/announcements", { params: { limit } }),
  getCuratedFeed: (limit, contentType) =>
    api.get("/home/curated-feed", { params: { limit, content_type: contentType } }),
};

export const priceCompareAPI = {
  search: (params) => api.get("/price-compare/search", { params: { ...params, platform: "web" } }),
  getCategories: () => api.get("/price-compare/categories"),
  trackClick: (data) => api.post("/price-compare/track-click", { ...data, platform: "web" }),
  getBrandCards: () => api.get("/price-compare/brand-cards"),
  saveProduct: (data) => api.post("/price-compare/saved", data),
  listSaved: () => api.get("/price-compare/saved"),
  deleteSaved: (id) => api.delete(`/price-compare/saved/${id}`),
  checkSaved: (params) => api.get("/price-compare/saved/check", { params }),
  getPriceHistory: (params) => api.get("/price-compare/price-history", { params }),
  adminRefreshPrices: () => api.post("/price-compare/admin/refresh-prices"),
  getRetailerVisibility: () => api.get("/price-compare/admin/retailer-visibility"),
  setRetailerVisibility: (id, hidden) => api.put(`/price-compare/admin/retailer-visibility/${id}`, null, { params: { hidden } }),
  getCurrencySettings: () => api.get("/price-compare/currency-settings"),
  setCurrencyModifier: (rate_modifier) => api.put("/price-compare/admin/currency-settings", null, { params: { rate_modifier } }),
  refreshCurrencyRate: () => api.post("/price-compare/admin/currency-settings/refresh"),
};

export const notificationsAPI = {
  list: (params) => api.get("/notifications", { params }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post("/notifications/mark-all-read"),
};

export const shippingLabelAPI = {
  adminListRequests: () => api.get("/shipping/admin/label-requests"),
  adminUploadLabel: (formData) => api.post("/shipping/admin/labels/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  adminMarkSuppliesShipped: (shippingPaymentId, formData) =>
    api.post(`/shipping/admin/supplies/${shippingPaymentId}/ship`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  sellerListLabels: () => api.get("/shipping/seller/labels"),
};

export const shippingAdminAPI = {
  listCategories: () => api.get("/shipping/admin/categories"),
  createCategory: (data) => api.post("/shipping/admin/categories", data),
  updateCategory: (categoryId, data) => api.put(`/shipping/admin/categories/${categoryId}`, data),
  deleteCategory: (categoryId) => api.delete(`/shipping/admin/categories/${categoryId}`),
  getConfig: () => api.get("/shipping/admin/config"),
  updateConfig: (data) => api.put("/shipping/admin/config", data),
};

export const listingSuppliesAPI = {
  getStatus: (listingId) => api.get(`/shipping/listings/${listingId}/supplies-status`),
  orderSupplies: (listingId) => api.post(`/shipping/listings/${listingId}/order-supplies`),
  confirm: (listingId, paymentId) => api.post(`/shipping/listings/${listingId}/order-supplies/confirm/${paymentId}`),
};

export const paymentsAPI = {
  getConfig: () => api.get("/payments/config"),
  createPaymentIntent: (data) => api.post("/payments/create-intent", data),
  confirmPayment: (paymentId) => api.post(`/payments/confirm/${paymentId}`),
  getOrder: (orderId) => api.get(`/orders/${orderId}`),
  getMyOrders: (params) => api.get("/orders", { params }),
  confirmDelivery: (orderId) => api.post(`/orders/${orderId}/confirm-delivery`),
  updateOrderStatus: (orderId, data) => api.put(`/orders/${orderId}/status`, data),
  openDispute: (orderId, data) => api.post(`/orders/${orderId}/dispute`, data),
  getEscrowInfo: (orderId) => api.get(`/orders/${orderId}/escrow-info`),
  adminMarkPaidOut: (orderId) => api.post(`/orders/${orderId}/mark-paid-out`),
};

export const userAPI = {
  getPublicProfile: (userId) => api.get(`/users/${userId}/public-profile`),
  getBySlug: (slug) => api.get(`/users/by-slug/${slug}`),
  getUserPosts: (userId) => api.get(`/users/${userId}/posts`),
  getUserListings: (userId) => api.get(`/users/${userId}/listings`),
  updateSettings: (allow) =>
    api.put(`/users/settings?allow_direct_messages=${allow}`),
  updateMuteShareRequests: (mute) =>
    api.put(`/users/settings/mute-share-requests?mute=${mute}`),
};

// ---- Points & Leaderboard ----
export const pointsAPI = {
  getBalance: () => api.get("/points/balance"),
  getHistory: (params) => api.get("/points/history", { params }),
  getLeaderboard: (limit = 50) => api.get("/points/leaderboard", { params: { limit } }),
  getRedemptionOptions: (category) => api.get("/points/redemption-options", { params: { category } }),
  submitRedemption: (data) => api.post("/points/redeem", data),
  getMyRedemptions: (params) => api.get("/points/my-redemptions", { params }),
  // admin
  adminStats: () => api.get("/points/admin/stats"),
  adminUsers: (params) => api.get("/points/admin/users", { params }),
  adminAdjust: (userId, data) => api.put(`/points/admin/users/${userId}/points`, data),
  adminRedemptionRequests: (params) => api.get("/points/admin/redemption-requests", { params }),
  adminApproveRedemption: (id, notes = "") => api.put(`/points/admin/redemption-requests/${id}/approve`, null, { params: { notes } }),
  adminRejectRedemption: (id, notes = "") => api.put(`/points/admin/redemption-requests/${id}/reject`, null, { params: { notes } }),
  adminGetOptions: (includeInactive) => api.get("/points/admin/redemption-options", { params: { include_inactive: includeInactive } }),
  adminCreateOption: (data) => api.post("/points/admin/redemption-options", data),
  adminUpdateOption: (id, data) => api.put(`/points/admin/redemption-options/${id}`, data),
  adminDeleteOption: (id) => api.delete(`/points/admin/redemption-options/${id}`),
  adminActionValues: () => api.get("/points/admin/action-values"),
  adminUpdateActionValue: (action, points) => api.put(`/points/admin/action-values/${action}`, { points }),
  adminSeedRewards: () => api.post("/points/admin/seed-standard-rewards"),
};

// ---- Clubs / Achievements ----
export const achievementsAPI = {
  getMyAchievements: () => api.get("/achievements/my-achievements"),
  getClubs: (category) => api.get("/achievements/clubs", { params: { category } }),
  getMyClubs: () => api.get("/achievements/my-clubs"),
  joinClub: (id) => api.post(`/achievements/clubs/${id}/join`),
  leaveClub: (id) => api.post(`/achievements/clubs/${id}/leave`),
  getClubMembers: (id, limit, skip) => api.get(`/achievements/clubs/${id}/members`, { params: { limit, skip } }),
  // admin
  adminGetClubs: () => api.get("/achievements/admin/clubs"),
  adminToggleClub: (id, data) => api.post(`/achievements/admin/clubs/${id}/toggle`, data),
  adminFeatureClub: (id, is_featured) => api.post(`/achievements/admin/clubs/${id}/feature`, { is_featured }),
};

// ---- iRacing ----
export const iracingAPI = {
  getConfig: () => api.get("/iracing/config"),
  getAuthUrl: () => api.get("/iracing/auth-url"),
  getStats: () => api.get("/iracing/stats"),
  unlink: () => api.post("/iracing/unlink"),
  mockLink: () => api.post("/iracing/mock-link"),
};

// ---- SimHub ----
export const simhubAPI = {
  pushTelemetry: (payload) => api.post("/simhub/telemetry/push", payload),
  getLatestTelemetry: () => api.get("/simhub/telemetry/latest"),
  getSessions: (params) => api.get("/simhub/sessions", { params }),
  getSession: (id) => api.get(`/simhub/sessions/${id}`),
  publishSession: (id) => api.post(`/simhub/sessions/${id}/publish`),
  unpublishSession: (id) => api.post(`/simhub/sessions/${id}/unpublish`),
  listPublicSessions: (params) => api.get("/simhub/sessions/public", { params }),
  getPublicSession: (id) => api.get(`/simhub/sessions/public/${id}`),
  createShareRequest: (session_id, message) => api.post("/simhub/share-requests", { session_id, message }),
  listIncomingShares: () => api.get("/simhub/share-requests/incoming"),
  listOutgoingShares: () => api.get("/simhub/share-requests/outgoing"),
  approveShare: (id) => api.post(`/simhub/share-requests/${id}/approve`),
  denyShare: (id) => api.post(`/simhub/share-requests/${id}/deny`),
  listApprovedSharedSessions: () => api.get("/simhub/share-requests/approved-sessions"),
  getApprovedSharedSession: (id) => api.get(`/simhub/share-requests/approved-sessions/${id}`),
  listDiscoverable: (params) => api.get("/simhub/sessions/discoverable", { params }),
  getMySponsors: () => api.get("/sponsorship/sponsors/me"),
};

export const sponsorAdminAPI = {
  listSponsors: () => api.get("/sponsorship/admin/sponsors"),
  createSponsor: (data) => api.post("/sponsorship/admin/sponsors", data),
  updateSponsor: (id, data) => api.put(`/sponsorship/admin/sponsors/${id}`, data),
  deleteSponsor: (id) => api.delete(`/sponsorship/admin/sponsors/${id}`),
  assignSponsors: (requestId, sponsor_ids) => api.put(`/sponsorship/admin/requests/${requestId}/sponsors`, { sponsor_ids }),
};

// ---- SimHub stats + misc ----
export const simhubStatsAPI = {
  getSummary: () => api.get("/simhub/stats/summary"),
  getUserProfile: () => api.get("/simhub/stats/user-profile"),
  getByGame: () => api.get("/simhub/stats/by-game"),
  getByCarClass: () => api.get("/simhub/stats/by-car-class"),
  getByTrack: () => api.get("/simhub/stats/by-track"),
  getPersonalBests: () => api.get("/simhub/personal-bests"),
  getLeaderboardTracks: () => api.get("/simhub/leaderboards/tracks"),
  getLeaderboard: (trackKey, params) => api.get(`/simhub/leaderboards/${encodeURIComponent(trackKey)}`, { params }),
  getMilestone: (id) => api.get(`/simhub/milestones/${id}`),
  listPendingMilestones: () => api.get("/simhub/milestones/pending"),
  dismissMilestone: (id) => api.post(`/simhub/milestones/${id}/dismiss`),
  shareMilestone: (id, content) => api.post(`/simhub/milestones/${id}/share`, { content }),
  listCustomClubs: () => api.get("/simhub/clubs/custom"),
  joinCustomClub: (id) => api.post(`/simhub/clubs/custom/${id}/join`),
  leaveCustomClub: (id) => api.post(`/simhub/clubs/custom/${id}/leave`),
  // Club hub (member pages, announcements, promotions, tournaments)
  clubPage: (id) => api.get(`/simhub/clubs/custom/${id}/page`),
  clubAnnouncements: (id) => api.get(`/simhub/clubs/custom/${id}/announcements`),
  createAnnouncement: (id, data) => api.post(`/simhub/clubs/custom/${id}/announcements`, data),
  deleteAnnouncement: (id, itemId) => api.delete(`/simhub/clubs/custom/${id}/announcements/${itemId}`),
  clubPromotions: (id) => api.get(`/simhub/clubs/custom/${id}/promotions`),
  createPromotion: (id, data) => api.post(`/simhub/clubs/custom/${id}/promotions`, data),
  deletePromotion: (id, itemId) => api.delete(`/simhub/clubs/custom/${id}/promotions/${itemId}`),
  clubTournaments: (id) => api.get(`/simhub/clubs/custom/${id}/tournaments`),
  createTournament: (id, data) => api.post(`/simhub/clubs/custom/${id}/tournaments`, data),
  tournamentDetail: (tid) => api.get(`/simhub/clubs/custom/tournaments/${tid}`),
  joinTournament: (tid) => api.post(`/simhub/clubs/custom/tournaments/${tid}/join`),
  leaveTournament: (tid) => api.post(`/simhub/clubs/custom/tournaments/${tid}/leave`),
  tournamentParticipants: (tid) => api.get(`/simhub/clubs/custom/tournaments/${tid}/participants`),
  syncTournament: (tid) => api.post(`/simhub/clubs/custom/tournaments/${tid}/sync`),
  overrideTournament: (tid, data) => api.post(`/simhub/clubs/custom/tournaments/${tid}/override`, data),
  messageTournament: (tid, data) => api.post(`/simhub/clubs/custom/tournaments/${tid}/message`, data),
  deleteTournament: (tid) => api.delete(`/simhub/clubs/custom/tournaments/${tid}`),
};
Object.assign(simhubAPI, simhubStatsAPI);

// ---- SimHub Admin ----
export const adminSimhubAPI = {
  listTracks: () => api.get("/simhub/admin/tracks"),
  updateTrack: (trackKey, data) => api.put(`/simhub/admin/tracks/${encodeURIComponent(trackKey)}`, data),
  analyticsCars: () => api.get("/simhub/admin/analytics/cars"),
  analyticsTracks: () => api.get("/simhub/admin/analytics/tracks"),
  carMembers: (carKey) => api.get(`/simhub/admin/analytics/cars/${encodeURIComponent(carKey)}/members`),
  trackMembers: (trackKey, params) => api.get(`/simhub/admin/analytics/tracks/${encodeURIComponent(trackKey)}/members`, { params }),
  clubSuggestions: (params) => api.get("/simhub/admin/club-suggestions", { params }),
  createClub: (data) => api.post("/simhub/admin/club-suggestions/create", data),
  listCustomClubs: () => api.get("/simhub/admin/custom-clubs"),
  updateCustomClub: (id, data) => api.put(`/simhub/admin/custom-clubs/${id}`, data),
  deleteCustomClub: (id) => api.delete(`/simhub/admin/custom-clubs/${id}`),
};

// ---- Media ----
export const mediaAPI = {
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
};

// Prefix relative /api/media URLs with the backend origin (needed outside same-origin).
export const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

// ---- Sponsorship ----
export const sponsorshipAPI = {
  getRequests: () => api.get("/sponsorship/requests"),
  createRequest: (data) => api.post("/sponsorship/requests", data),
  updateRequest: (id, data) => api.patch(`/sponsorship/requests/${id}`, data),
  requestTierUpgrade: (id, data) => api.post(`/sponsorship/requests/${id}/tier-upgrade`, data),
  getTerms: () => api.get("/sponsorship/terms"),
  acceptDeal: (id) => api.post(`/sponsorship/requests/${id}/accept-deal`, { accepted: true }),
  getMessages: (requestId) => api.get(`/sponsorship/requests/${requestId}/messages`),
  sendMessage: (requestId, content) =>
    api.post(`/sponsorship/requests/${requestId}/message?content=${encodeURIComponent(content)}`),
};

// ---- Stripe Connect (payouts admin) ----
export const connectAPI = {
  getStatus: () => api.get("/connect/status"),
  adminPayouts: (params) => api.get("/connect/admin/payouts", { params }),
  adminConnectedSellers: () => api.get("/connect/admin/connected-sellers"),
  adminReleasePayout: (txId) => api.post(`/connect/admin/payouts/${txId}/release`),
  adminHoldPayout: (txId) => api.post(`/connect/admin/payouts/${txId}/hold`),
  adminRefundPayout: (txId) => api.post(`/connect/admin/payouts/${txId}/refund`),
};

// ---- Shipping (admin config) ----
Object.assign(shippingAPI, {
  getAdminConfig: () => api.get("/shipping/admin/config"),
  updateAdminConfig: (config) => api.put("/shipping/admin/config", config),
  getAdminCategories: () => api.get("/shipping/admin/categories"),
  updateCategory: (id, data) => api.put(`/shipping/admin/categories/${id}`, data),
  createCategory: (data) => api.post("/shipping/admin/categories", data),
  deleteCategory: (id) => api.delete(`/shipping/admin/categories/${id}`),
});

// ---- Price Compare (admin) ----
export const priceCompareAdminAPI = {
  getRetailers: () => api.get("/price-compare/admin/retailers"),
  createRetailer: (data) => api.post("/price-compare/admin/retailers", data),
  deleteRetailer: (id) => api.delete(`/price-compare/admin/retailers/${id}`),
  getTags: () => api.get("/price-compare/admin/tags"),
  createTag: (data) => api.post("/price-compare/admin/tags", data),
  deleteTag: (id) => api.delete(`/price-compare/admin/tags/${id}`),
  getBrandCards: () => api.get("/price-compare/admin/brand-cards"),
  createBrandCard: (data) => api.post("/price-compare/admin/brand-cards", data),
  updateBrandCard: (id, data) => api.put(`/price-compare/admin/brand-cards/${id}`, data),
  deleteBrandCard: (id) => api.delete(`/price-compare/admin/brand-cards/${id}`),
  uploadBrandCardImage: (file) => {
    const form = new FormData();
    form.append("file", file);
    return api.post("/price-compare/admin/brand-cards/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  uploadPriceCompareProducts: (file, replace = false) => {
    const form = new FormData();
    form.append("file", file);
    return api.post(`/price-compare/admin/products/upload?replace=${replace ? "true" : "false"}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  priceCompareProductsSummary: () => api.get("/price-compare/admin/products/summary"),
  clearPriceCompareProducts: () => api.delete("/price-compare/admin/products"),
  getAnalytics: (days = 30) => api.get("/price-compare/admin/analytics", { params: { days } }),
  exportAnalytics: (days = 30) => api.get("/price-compare/admin/analytics/export", { params: { days }, responseType: "blob" }),
  getRetailerVisibility: () => api.get("/price-compare/admin/retailer-visibility"),
  setRetailerVisibility: (id, hidden) => api.put(`/price-compare/admin/retailer-visibility/${id}`, null, { params: { hidden } }),
  getCurrencySettings: () => api.get("/price-compare/currency-settings"),
  setCurrencyModifier: (rate_modifier) => api.put("/price-compare/admin/currency-settings", null, { params: { rate_modifier } }),
  refreshCurrencyRate: () => api.post("/price-compare/admin/currency-settings/refresh"),
};

// ---- Central Product Catalog ----
export const equipmentAPI = {
  getManufacturers: (category) => api.get("/equipment/manufacturers", { params: category ? { category } : {} }),
};

export const catalogAPI = {
  getProducts: () => api.get("/catalog/products"),
  addProduct: (data) => api.post("/catalog/products", data),
  deleteProduct: (id) => api.delete(`/catalog/products/${id}`),
  uploadCSV: (file) => {
    const form = new FormData();
    form.append("file", file);
    return api.post("/catalog/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  exportCSV: () => api.get("/catalog/export", { responseType: "blob" }),
  getGear: () => api.get("/catalog/gear"),
  addGear: (data) => api.post("/catalog/gear", data),
  deleteGear: (id) => api.delete(`/catalog/gear/${id}`),
};

// ---- Moderation ----
export const moderationAPI = {
  report: (data) => api.post("/moderation/report", data),
  myItems: () => api.get("/moderation/my-items"),
  // admin
  listItems: (params) => api.get("/admin/moderation/items", { params }),
  approve: (id) => api.post(`/admin/moderation/items/${id}/approve`),
  remove: (id) => api.post(`/admin/moderation/items/${id}/remove`),
  hide: (id) => api.post(`/admin/moderation/items/${id}/hide`),
  messageAuthor: (id, text) => api.post(`/admin/moderation/items/${id}/message`, { text }),
  getSettings: () => api.get("/admin/moderation/settings"),
  updateSettings: (data) => api.put("/admin/moderation/settings", data),
  analytics: () => api.get("/admin/moderation/analytics"),
};

// ---- Admin ----
export const adminAPI = {
  getStats: () => api.get("/admin/stats"),
  getUsers: (params) => api.get("/admin/users", { params }),
  banUser: (userId, banned) => api.put(`/admin/users/${userId}/ban`, null, { params: { ban: banned } }),
  deleteAccount: (userId) => api.delete(`/admin/accounts/${userId}`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  setRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
  getPendingAccounts: () => api.get("/admin/pending-accounts"),
  approveAccount: (userId, action) => api.put(`/admin/accounts/${userId}/approve?action=${action}`),
  updateUserSlug: (userId, slug) => api.put(`/admin/users/${userId}/slug?slug=${encodeURIComponent(slug)}`),
  setUserCategory: (userId, data) => api.put(`/admin/users/${userId}/member-category`, data),
  // listings moderation
  getListings: (params) => api.get("/admin/listings", { params }),
  deleteListing: (id) => api.delete(`/admin/listings/${id}`),
  updateListingStatus: (id, status, reason = "") => api.put(`/admin/listings/${id}/status?status=${status}&reason=${encodeURIComponent(reason)}`),
  flagListing: (id, isFlagged = true) => api.put(`/admin/listings/${id}/flag?is_flagged=${isFlagged}`),
  getListingStats: () => api.get("/admin/listing-stats"),
  // forum moderation
  getForumPosts: (params) => api.get("/admin/forum-posts", { params }),
  deleteForumPost: (id) => api.delete(`/admin/forum-posts/${id}?notify_user=true`),
  flagForumPost: (id, isFlagged = true) => api.put(`/admin/forum-posts/${id}/flag?is_flagged=${isFlagged}`),
  getForumReports: (params) => api.get("/admin/forum-reports", { params }),
  getForumStats: () => api.get("/admin/forum-stats"),
  // social moderation
  getSocialPosts: (params) => api.get("/admin/social-posts", { params }),
  deleteSocialPost: (id) => api.delete(`/admin/social-posts/${id}?notify_user=true`),
  getSocialStats: () => api.get("/admin/social-stats"),
  // sponsorships
  getSponsorships: (params) => api.get("/admin/sponsorships", { params }),
  updateSponsorshipStatus: (id, status) => api.put(`/admin/sponsorships/${id}/status?status=${status}`),
  sendSponsorshipDeal: (id, data) => api.post(`/admin/sponsorships/${id}/send-deal`, data),
  getSponsorshipTerms: () => api.get("/admin/sponsorship-terms"),
  updateSponsorshipTerms: (content) => api.put("/admin/sponsorship-terms", { content }),
  setSponsorshipTier: (id, tier) => {
    const form = new FormData();
    form.append("tier", tier);
    return api.post(`/admin/sponsorships/${id}/tier`, form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  denySponsorshipTier: (id) => api.post(`/admin/sponsorships/${id}/deny-tier`),
  clearSponsorshipUpdates: (id) => api.post(`/admin/sponsorships/${id}/clear-updates`),
  rejectSponsorshipUpdates: (id, message = "") => api.post(`/admin/sponsorships/${id}/reject-updates?message=${encodeURIComponent(message)}`),
  sendSponsorshipMessage: (id, content) => api.post(`/admin/sponsorships/${id}/message?content=${encodeURIComponent(content)}`),
  // announcements
  listAnnouncements: () => api.get("/admin/announcements"),
  createAnnouncement: (data) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") form.append(k, v); });
    return api.post("/admin/announcements", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  updateAnnouncement: (id, data) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v !== null && v !== undefined) form.append(k, v); });
    return api.patch(`/admin/announcements/${id}`, form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  toggleAnnouncement: (id) => api.post(`/admin/announcements/${id}/toggle`),
  deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),
  // settings
  getSettings: () => api.get("/admin/settings"),
  updateSettings: (data) => api.put("/admin/settings", null, { params: data }),
  // curated content (News & Hot Content)
  getCuratedContent: () => api.get("/admin/curated-content/all"),
  createCuratedContent: (data) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") form.append(k, v); });
    return api.post("/admin/curated-content", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  updateCuratedContent: (id, data) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v !== null && v !== undefined) form.append(k, v); });
    return api.put(`/admin/curated-content/${id}`, form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  togglePinCuratedContent: (id) => api.put(`/admin/curated-content/${id}/pin`),
  deleteCuratedContent: (id) => api.delete(`/admin/curated-content/${id}`),
  notifyCuratedContent: (id, segment = "all", force = false) => {
    const form = new FormData();
    form.append("segment", segment);
    form.append("force", force);
    return api.post(`/admin/curated-content/${id}/notify`, form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  getNotificationSegments: () => api.get("/admin/curated-content/segments"),
  uploadCuratedImage: (file) => {
    const form = new FormData();
    form.append("file", file);
    return api.post("/admin/curated-content/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  parseYouTubeUrl: (url) => {
    const form = new FormData();
    form.append("url", url);
    return api.post("/admin/parse-youtube-url", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  // staff permissions
  getMyPermissions: () => api.get("/admin/my-permissions"),
  listStaff: () => api.get("/admin/staff"),
  setStaffPermissions: (userId, body) => api.put(`/admin/staff/${userId}/permissions`, body),
  // legal pages + contact
  updateLegal: (slug, body) => api.put(`/admin/legal/${slug}`, body),
  getContactSubmissions: () => api.get("/admin/contact-submissions"),
  markContactRead: (id) => api.patch(`/admin/contact-submissions/${id}/read`),
};

export const legalAPI = {
  get: (slug) => api.get(`/legal/${slug}`),
  submitContact: (data) => api.post("/contact", data),
};

export const checkoutAPI = {
  createSession: (kind, refId) =>
    api.post("/checkout/session", { kind, ref_id: refId, origin_url: window.location.origin }),
  getStatus: (sessionId) => api.get(`/checkout/status/${sessionId}`),
};

export const achievementConfigsAPI = {
  list: () => api.get("/achievement-configs"),
  update: (type, data) => api.put(`/achievement-configs/${type}`, data),
  uploadBackground: (type, formData) =>
    api.post(`/achievement-configs/${type}/background`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

export default api;
